// API configuration for MongoDB backend
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://127.0.0.1:5005/api';

// Log API URL for debugging (remove in production)
console.log('🔗 API Base URL:', API_BASE_URL);

interface ApiResponse<T> {
    data?: T;
    error?: string;
}

function unwrapCollectionResponse<T>(
    payload: unknown,
    key: string
): T[] {
    if (Array.isArray(payload)) {
        return payload as T[];
    }

    if (payload && typeof payload === 'object') {
        const collection = (payload as Record<string, unknown>)[key];
        if (Array.isArray(collection)) {
            return collection as T[];
        }
    }

    return [];
}

// Helper function for API calls
async function apiCall<T>(
    endpoint: string,
    options: RequestInit = {}
): Promise<ApiResponse<T>> {
    try {
        const token = localStorage.getItem('auth_token');
        const method = (options.method || 'GET').toUpperCase();
        const isFormDataBody = typeof FormData !== 'undefined' && options.body instanceof FormData;
        const shouldBypassProductCache = method === 'GET' && endpoint.startsWith('/products');
        const fullUrl = shouldBypassProductCache
            ? `${API_BASE_URL}${endpoint}${endpoint.includes('?') ? '&' : '?'}_=${Date.now()}`
            : `${API_BASE_URL}${endpoint}`;

        const headers: HeadersInit = {
            ...(token && { Authorization: `Bearer ${token}` }),
            ...((options.body !== undefined || method !== 'GET') && !isFormDataBody ? { 'Content-Type': 'application/json' } : {}),
            ...options.headers,
        };

        console.log(`🌐 API Call: ${method} ${fullUrl}`);

        const response = await fetch(fullUrl, {
            ...options,
            cache: shouldBypassProductCache ? 'no-store' : options.cache,
            headers,
        });

        console.log(`📡 Response Status: ${response.status} ${response.statusText}`);

        // Handle non-JSON responses
        let data;
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
            data = await response.json();
        } else {
            const text = await response.text();
            console.log(`📄 Response Text:`, text.substring(0, 200));
            try {
                data = JSON.parse(text);
            } catch {
                data = { error: text || 'Invalid response format' };
            }
        }

        console.log(`📦 Response Data:`, data);

        if (!response.ok) {
            console.error('❌ API Error:', endpoint, response.status, data);
            const errorMessage = data?.error || data?.message || 'An error occurred';
            return { error: errorMessage };
        }

        return { data };
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Network error';
        console.error('❌ API call failed:', endpoint, errorMessage, error);
        return { error: errorMessage };
    }
}

// Auth API
export const authApi = {
    async login(email: string, password: string, adminLogin?: boolean) {
        return apiCall<{ user: any; token: string; requiresPasswordReset?: boolean }>('/auth/login', {
            method: 'POST',
            body: JSON.stringify({ email, password, adminLogin }),
        });
    },

    async register(email: string, password: string, name: string, phone?: string, acceptedPolicies?: boolean) {
        return apiCall<{ user: any; token: string }>('/auth/register', {
            method: 'POST',
            body: JSON.stringify({ email, password, name, phone, acceptedPolicies }),
        });
    },

    async getProfile() {
        return apiCall<{ user: any }>('/auth/me');
    },

    async updateProfile(data: { name?: string; phone?: string; address?: any }) {
        return apiCall<{ user: any }>('/auth/profile', {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    },

    async sendVerificationCode(email: string, purpose?: string, extra?: Record<string, any>) {
        return apiCall<{ message: string }>('/auth/send-verification-code', {
            method: 'POST',
            body: JSON.stringify({ email, purpose, ...extra }),
        });
    },

    async verifyEmail(email: string, code: string) {
        return apiCall<{ user: any; token: string }>('/auth/verify-email', {
            method: 'POST',
            body: JSON.stringify({ email, code }),
        });
    },

    async forgotPassword(email: string) {
        return apiCall<{ message: string; email?: string }>('/auth/forgot-password', {
            method: 'POST',
            body: JSON.stringify({ email }),
        });
    },

    async resetPassword(email: string, otp: string, newPassword: string) {
        return apiCall<{ message: string }>('/auth/reset-password', {
            method: 'POST',
            body: JSON.stringify({ email, otp, newPassword }),
        });
    },

    async resendPasswordResetOTP(email: string) {
        return apiCall<{ message: string }>('/auth/resend-password-reset-otp', {
            method: 'POST',
            body: JSON.stringify({ email }),
        });
    },

    // Address Management
    async getAddresses() {
        return apiCall<{ addresses: any[] }>('/auth/addresses');
    },

    async addAddress(address: any) {
        return apiCall<{ address: any }>('/auth/addresses', {
            method: 'POST',
            body: JSON.stringify(address),
        });
    },

    async updateAddress(addressId: string, address: any) {
        return apiCall<{ address: any }>(`/auth/addresses/${addressId}`, {
            method: 'PUT',
            body: JSON.stringify(address),
        });
    },

    async deleteAddress(addressId: string) {
        return apiCall<void>(`/auth/addresses/${addressId}`, { method: 'DELETE' });
    },

    async setDefaultAddress(addressId: string) {
        return apiCall<{ addresses: any[] }>(`/auth/addresses/${addressId}/default`, {
            method: 'PUT',
        });
    },

    logout() {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
    },
};

// Products API
export const productsApi = {
    async getAll(params?: {
        category?: string;
        featured?: boolean;
        bestseller?: boolean;
        search?: string;
        page?: number;
        limit?: number;
        sort?: string;
    }) {
        const searchParams = new URLSearchParams();
        if (params) {
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined) {
                    // Convert boolean to string for featured and bestseller
                    if ((key === 'featured' || key === 'bestseller') && typeof value === 'boolean') {
                        searchParams.append(key, value ? 'true' : 'false');
                    } else {
                        searchParams.append(key, String(value));
                    }
                }
            });
        }
        const query = searchParams.toString() ? `?${searchParams.toString()}` : '';
        return apiCall<{ products: any[]; pagination: any }>(`/products${query}`);
    },

    async getById(id: string) {
        return apiCall<{ product: any }>(`/products/${id}`);
    },

    async create(product: any) {
        return apiCall<{ product: any }>('/products', {
            method: 'POST',
            body: JSON.stringify(product),
        });
    },

    async bulkCreate(products: any[]) {
        return apiCall<{ results: any[]; message?: string }>('/products/bulk', {
            method: 'POST',
            body: JSON.stringify({ products }),
        });
    },

    async update(id: string, product: any) {
        return apiCall<{ product: any }>(`/products/${id}`, {
            method: 'PUT',
            body: JSON.stringify(product),
        });
    },

    async delete(id: string) {
        return apiCall<void>(`/products/${id}`, { method: 'DELETE' });
    },
};

// Newsletter API
export const newsletterApi = {
    async subscribe(email: string) {
        return apiCall<{ message: string; assignedCouponCode?: string | null; subscriber?: any }>('/newsletter/subscribe', {
            method: 'POST',
            body: JSON.stringify({ email }),
        });
    },

    async list() {
        return apiCall<{ subscribers: any[] }>('/newsletter', { method: 'GET' });
    },

    async remove(id: string) {
        return apiCall<{ message: string }>(`/newsletter/${id}`, { method: 'DELETE' });
    },

    async listTemplates() {
        return apiCall<{ templates: any[] }>('/newsletter/templates', { method: 'GET' });
    },

    async createTemplate(payload: { name: string; subject: string; html: string }) {
        return apiCall<{ template: any }>('/newsletter/templates', {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    },

    async updateTemplate(id: string, payload: { name: string; subject: string; html: string }) {
        return apiCall<{ template: any }>(`/newsletter/templates/${id}`, {
            method: 'PUT',
            body: JSON.stringify(payload),
        });
    },

    async deleteTemplate(id: string) {
        return apiCall<{ message: string }>(`/newsletter/templates/${id}`, { method: 'DELETE' });
    },

    async sendTemplate(payload: { subscriberId?: string; email?: string; templateId: string }) {
        return apiCall<{ message: string }>('/newsletter/send', {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    },
};

// Contact Submissions API
export const contactApi = {
    async submit(payload: { name: string; email: string; phone?: string; message: string }) {
        return apiCall<{ submission: any; message?: string }>('/contact-submissions', {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    },

    async list() {
        return apiCall<{ submissions: any[] }>('/contact-submissions');
    },

    async delete(id: string) {
        return apiCall<{ message?: string }>(`/contact-submissions/${id}`, {
            method: 'DELETE',
        });
    },
};

// Attributes API
export const attributesApi = {
    async getAll() {
        return apiCall<{ attributes: any[] }>('/attributes');
    },
    async create(name: string) {
        return apiCall<{ attribute: any }>('/attributes', {
            method: 'POST',
            body: JSON.stringify({ name }),
        });
    },
    async delete(id: string) {
        return apiCall<void>(`/attributes/${id}`, { method: 'DELETE' });
    }
};

// Categories API
export const categoriesApi = {
    async getAll() {
        const response = await apiCall<any>('/categories');
        if (response.error) {
            return response as ApiResponse<{ categories: any[] }>;
        }

        return {
            data: {
                categories: unwrapCollectionResponse<any>(response.data, 'categories'),
            },
        };
    },

    async getById(id: string) {
        return apiCall<{ category: any }>(`/categories/${id}`);
    },

    async create(category: any) {
        return apiCall<{ category: any }>('/categories', {
            method: 'POST',
            body: JSON.stringify(category),
        });
    },

    async update(id: string, category: any) {
        return apiCall<{ category: any }>(`/categories/${id}`, {
            method: 'PUT',
            body: JSON.stringify(category),
        });
    },

    async delete(id: string) {
        return apiCall<{ message?: string; archived?: boolean; productCount?: number }>(`/categories/${id}`, { method: 'DELETE' });
    },
};

// Orders API
export const ordersApi = {
    async getAll() {
        return apiCall<{ orders: any[] }>('/orders');
    },

    async getById(id: string) {
        return apiCall<{ order: any }>(`/orders/${id}`);
    },

    async getInvoiceAsset(
        id: string,
        options?: { admin?: boolean; format?: 'html' | 'pdf'; download?: boolean }
    ) {
        try {
            const token = localStorage.getItem('auth_token');
            const query = new URLSearchParams();
            query.set('format', options?.format || 'pdf');
            if (options?.download) {
                query.set('download', 'true');
            }

            const path = options?.admin
                ? `${API_BASE_URL}/orders/admin/${id}/invoice?${query.toString()}`
                : `${API_BASE_URL}/orders/${id}/invoice?${query.toString()}`;

            const response = await fetch(path, {
                method: 'GET',
                headers: {
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
            });

            if (!response.ok) {
                let errorMessage = 'Failed to download invoice';
                try {
                    const payload = await response.json();
                    errorMessage = payload?.error || payload?.message || errorMessage;
                } catch {
                    const text = await response.text();
                    errorMessage = text || errorMessage;
                }
                return { error: errorMessage };
            }

            const blob = await response.blob();
            const disposition = response.headers.get('content-disposition') || '';
            const match = disposition.match(/filename=\"?([^"]+)\"?/i);
            const fallbackExtension = (options?.format || 'pdf') === 'pdf' ? 'pdf' : 'html';
            const filename = match?.[1] || `invoice-${id}.${fallbackExtension}`;

            return {
                data: {
                    blob,
                    filename,
                    contentType: response.headers.get('content-type') || '',
                },
            };
        } catch (error) {
            return {
                error: error instanceof Error ? error.message : 'Failed to download invoice',
            };
        }
    },

    async downloadInvoice(id: string) {
        return this.getInvoiceAsset(id, { format: 'pdf', download: true });
    },

    async viewInvoice(id: string) {
        return this.getInvoiceAsset(id, { format: 'html', download: false });
    },

    async downloadAdminInvoice(id: string) {
        return this.getInvoiceAsset(id, { admin: true, format: 'pdf', download: true });
    },

    async viewAdminInvoice(id: string) {
        return this.getInvoiceAsset(id, { admin: true, format: 'html', download: false });
    },

    async create(orderData: { items?: any[]; shippingAddress: any; paymentMethod: string; notes?: string }) {
        return apiCall<{ order: any; message?: string }>('/orders', {
            method: 'POST',
            body: JSON.stringify(orderData),
        });
    },

    async createPaymentSession(orderData: { items?: any[]; shippingAddress: any; paymentMethod: string; notes?: string; couponCode?: string | null; couponDiscount?: number }) {
        return apiCall<{ order: any; message?: string; paymentSessionId: string; paymentGatewayOrderId: string; returnUrl?: string }>('/orders/payment-session', {
            method: 'POST',
            body: JSON.stringify(orderData),
        });
    },

    async getPaymentStatus(id: string) {
        return apiCall<{ status: 'pending' | 'paid' | 'failed'; gatewayOrderStatus?: string; order: any }>(`/orders/${id}/payment-status`);
    },

    async cancel(id: string) {
        return apiCall<{ order: any }>(`/orders/${id}/cancel`, { method: 'PUT' });
    },

    async requestRefund(id: string, payload: FormData) {
        return apiCall<{ order: any; message?: string }>(`/orders/${id}/refund-request`, {
            method: 'POST',
            body: payload,
        });
    },

    // Admin endpoints
    async getAllAdmin(params?: { status?: string; page?: number; limit?: number }) {
        const searchParams = new URLSearchParams();
        if (params) {
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined) searchParams.append(key, String(value));
            });
        }
        const query = searchParams.toString() ? `?${searchParams.toString()}` : '';
        return apiCall<{ orders: any[]; pagination: any }>(`/orders/admin/all${query}`);
    },

    async updateStatus(id: string, status: { orderStatus?: string; paymentStatus?: string }) {
        return apiCall<{ order: any }>(`/orders/admin/${id}/status`, {
            method: 'PUT',
            body: JSON.stringify(status),
        });
    },

    async reviewRefund(id: string, payload: { action: 'approve' | 'reject'; adminNote?: string }) {
        return apiCall<{ order: any; message?: string }>(`/orders/admin/${id}/refund/review`, {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    },

    async markReturnReceived(id: string, payload?: { adminNote?: string }) {
        return apiCall<{ order: any; message?: string }>(`/orders/admin/${id}/refund/mark-return-received`, {
            method: 'POST',
            body: JSON.stringify(payload || {}),
        });
    },

    async initiateRefund(id: string, payload?: { refundAmount?: number; adminNote?: string; mode?: 'manual' | 'gateway' }) {
        return apiCall<{ order: any; message?: string }>(`/orders/admin/${id}/refund/initiate`, {
            method: 'POST',
            body: JSON.stringify(payload || {}),
        });
    },

    async syncRefund(id: string) {
        return apiCall<{ order: any; refund?: any; message?: string }>(`/orders/admin/${id}/refund/sync`);
    },

    async completeRefund(id: string, payload?: { reference?: string; adminNote?: string }) {
        return apiCall<{ order: any; message?: string }>(`/orders/admin/${id}/refund/complete`, {
            method: 'POST',
            body: JSON.stringify(payload || {}),
        });
    },

    async createShiprocketShipment(id: string) {
        return apiCall<{ order: any; message?: string }>(`/orders/admin/${id}/shiprocket/create`, {
            method: 'POST',
        });
    },

    async getShiprocketTracking(id: string) {
        return apiCall<{ order: any; tracking: any }>(`/orders/admin/${id}/shiprocket/track`);
    },
};

// Cart API
export const cartApi = {
    async get() {
        return apiCall<{ cart: any }>('/cart');
    },

    async addItem(productId: string, quantity: number = 1) {
        return apiCall<{ cart: any }>('/cart/add', {
            method: 'POST',
            body: JSON.stringify({ productId, quantity }),
        });
    },

    async updateQuantity(productId: string, quantity: number) {
        return apiCall<{ cart: any }>('/cart/update', {
            method: 'PUT',
            body: JSON.stringify({ productId, quantity }),
        });
    },

    async removeItem(productId: string) {
        return apiCall<{ cart: any }>(`/cart/remove/${productId}`, { method: 'DELETE' });
    },

    async clear() {
        return apiCall<void>('/cart/clear', { method: 'DELETE' });
    },
};

// Banners API
export const bannersApi = {
    async getAll(params?: { position?: string; active?: boolean }) {
        const searchParams = new URLSearchParams();
        if (params) {
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined) searchParams.append(key, String(value));
            });
        }
        const query = searchParams.toString() ? `?${searchParams.toString()}` : '';
        const response = await apiCall<any>(`/banners${query}`);
        if (response.error) {
            return response as ApiResponse<{ banners: any[] }>;
        }

        return {
            data: {
                banners: unwrapCollectionResponse<any>(response.data, 'banners'),
            },
        };
    },

    async getById(id: string) {
        return apiCall<{ banner: any }>(`/banners/${id}`);
    },

    async create(banner: any) {
        return apiCall<{ banner: any }>('/banners', {
            method: 'POST',
            body: JSON.stringify(banner),
        });
    },

    async update(id: string, banner: any) {
        return apiCall<{ banner: any }>(`/banners/${id}`, {
            method: 'PUT',
            body: JSON.stringify(banner),
        });
    },

    async delete(id: string) {
        return apiCall<void>(`/banners/${id}`, { method: 'DELETE' });
    },
};

// Promo Blocks API (Poojan Booking, Astro Consulting, etc.)
export const promoBlocksApi = {
    async getAll(params?: { active?: boolean }) {
        const searchParams = new URLSearchParams();
        if (params) {
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined) searchParams.append(key, String(value));
            });
        }
        const query = searchParams.toString() ? `?${searchParams.toString()}` : '';
        return apiCall<{ blocks: any[] }>(`/promo-blocks${query}`);
    },

    async getById(id: string) {
        return apiCall<{ block: any }>(`/promo-blocks/${id}`);
    },

    async create(block: any) {
        return apiCall<{ block: any }>('/promo-blocks', {
            method: 'POST',
            body: JSON.stringify(block),
        });
    },

    async update(id: string, block: any) {
        return apiCall<{ block: any }>(`/promo-blocks/${id}`, {
            method: 'PUT',
            body: JSON.stringify(block),
        });
    },

    async delete(id: string) {
        return apiCall<void>(`/promo-blocks/${id}`, { method: 'DELETE' });
    },
};

// Section Videos API (full-width video section, auto-play sequence)
export const sectionVideosApi = {
    async getAll(params?: { active?: boolean }) {
        const searchParams = new URLSearchParams();
        if (params) {
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined) searchParams.append(key, String(value));
            });
        }
        const query = searchParams.toString() ? `?${searchParams.toString()}` : '';
        return apiCall<{ videos: any[] }>(`/section-videos${query}`);
    },

    async getById(id: string) {
        return apiCall<{ video: any }>(`/section-videos/${id}`);
    },

    async create(video: any) {
        return apiCall<{ video: any }>('/section-videos', {
            method: 'POST',
            body: JSON.stringify(video),
        });
    },

    async update(id: string, video: any) {
        return apiCall<{ video: any }>(`/section-videos/${id}`, {
            method: 'PUT',
            body: JSON.stringify(video),
        });
    },

    async delete(id: string) {
        return apiCall<void>(`/section-videos/${id}`, { method: 'DELETE' });
    },
};

// Panchang API
export const panchangApi = {
    async getToday() {
        return apiCall<{ panchang: any }>('/panchang/today');
    },

    async getByDate(date: string) {
        return apiCall<{ panchang: any }>(`/panchang/date/${date}`);
    },

    async fetchFromAPI(latitude: number, longitude: number, timezone?: number) {
        const now = new Date();
        return apiCall<{ panchang: any }>('/panchang/fetch', {
            method: 'POST',
            body: JSON.stringify({
                latitude,
                longitude,
                timezone: timezone || now.getTimezoneOffset() / -60,
                year: now.getFullYear(),
                month: now.getMonth() + 1,
                date: now.getDate(),
                hours: now.getHours(),
                minutes: now.getMinutes(),
                seconds: now.getSeconds(),
            }),
        });
    },

    async getAll() {
        return apiCall<{ panchang: any[] }>('/panchang');
    },

    async create(panchang: any) {
        return apiCall<{ panchang: any }>('/panchang', {
            method: 'POST',
            body: JSON.stringify(panchang),
        });
    },

    async update(id: string, panchang: any) {
        return apiCall<{ panchang: any }>(`/panchang/${id}`, {
            method: 'PUT',
            body: JSON.stringify(panchang),
        });
    },

    async delete(id: string) {
        return apiCall<void>(`/panchang/${id}`, { method: 'DELETE' });
    },
};

// Coupons API
export const couponsApi = {
    async getActive() {
        return apiCall<{ coupons: any[] }>('/coupons/active');
    },

    async validate(payload: { code: string; email?: string; cartTotal?: number }) {
        return apiCall<{ coupon: any }>('/coupons/validate', {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    },

    async getAll() {
        return apiCall<{ coupons: any[] }>('/coupons');
    },

    async getById(id: string) {
        return apiCall<{ coupon: any }>(`/coupons/${id}`);
    },

    async create(coupon: any) {
        return apiCall<{ coupon: any }>('/coupons', {
            method: 'POST',
            body: JSON.stringify(coupon),
        });
    },

    async update(id: string, coupon: any) {
        return apiCall<{ coupon: any }>(`/coupons/${id}`, {
            method: 'PUT',
            body: JSON.stringify(coupon),
        });
    },

    async delete(id: string) {
        return apiCall<void>(`/coupons/${id}`, { method: 'DELETE' });
    },
};

// Customers API
export const customersApi = {
    async getAll() {
        return apiCall<{ customers: any[] }>('/customers');
    },

    async getById(id: string) {
        return apiCall<{ customer: any; orders: any[] }>(`/customers/${id}`);
    },
};

// Settings API
export const settingsApi = {
    async get() {
        return apiCall<{ settings: any }>('/settings');
    },

    async update(settings: any) {
        return apiCall<{ settings: any; message?: string }>('/settings', {
            method: 'PUT',
            body: JSON.stringify(settings),
        });
    },
};

// Festivals API
export const festivalsApi = {
    async getAll() {
        return apiCall<{ festivals: any[] }>('/festivals');
    },
    async getById(id: string) {
        return apiCall<{ festival: any }>(`/festivals/${id}`);
    },
    async create(festival: any) {
        return apiCall<{ festival: any }>('/festivals', {
            method: 'POST',
            body: JSON.stringify(festival),
        });
    },
    async update(id: string, festival: any) {
        return apiCall<{ festival: any }>(`/festivals/${id}`, {
            method: 'PUT',
            body: JSON.stringify(festival),
        });
    },
    async delete(id: string) {
        return apiCall<void>(`/festivals/${id}`, { method: 'DELETE' });
    },
};

// Shiprocket API
export const shiprocketApi = {
    async getAccount() {
        return apiCall<any>('/shiprocket/account');
    },

    async getDebugConfig() {
        return apiCall<any>('/shiprocket/debug-config');
    },

    async getWallet() {
        return apiCall<any>('/shiprocket/wallet');
    },

    async getCourierIntegrations() {
        return apiCall<any[]>('/shiprocket/couriers/integrations');
    },

    async getPickupAddresses() {
        return apiCall<any[]>('/shiprocket/pickup-addresses');
    },

    async getOrders(status?: string) {
        const searchParams = new URLSearchParams();
        if (status) {
            searchParams.append('status', status);
        }

        const query = searchParams.toString() ? `?${searchParams.toString()}` : '';
        return apiCall<any[]>(`/shiprocket/orders${query}`);
    },

    async getOrderDetails(orderId: string) {
        return apiCall<any>(`/shiprocket/orders/${orderId}`);
    },

    async createOrder(payload: any) {
        return apiCall<any>('/shiprocket/orders/create', {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    },

    async updateOrder(orderId: string, payload: any) {
        return apiCall<any>(`/shiprocket/orders/${orderId}`, {
            method: 'PUT',
            body: JSON.stringify(payload),
        });
    },

    async cancelOrder(orderId: string) {
        return apiCall<any>(`/shiprocket/orders/${orderId}/cancel`, {
            method: 'POST',
        });
    },

    async getShipmentDetails(shipmentId: string) {
        return apiCall<any>(`/shiprocket/shipments/${shipmentId}`);
    },

    async assignAwb(shipmentId: string, courierId?: string) {
        return apiCall<any>('/shiprocket/shipments/assign-awb', {
            method: 'POST',
            body: JSON.stringify({ shipmentId, courierId }),
        });
    },

    async generatePickup(shipmentId: string) {
        return apiCall<any>('/shiprocket/shipments/generate-pickup', {
            method: 'POST',
            body: JSON.stringify({ shipmentId }),
        });
    },

    async cancelShipment(shipmentId: string) {
        return apiCall<any>('/shiprocket/shipments/cancel', {
            method: 'POST',
            body: JSON.stringify({ shipmentId }),
        });
    },

    async trackByAwb(awbCode: string) {
        return apiCall<any>(`/shiprocket/track/awb/${encodeURIComponent(awbCode)}`);
    },

    async trackByOrderId(orderId: string) {
        return apiCall<any>(`/shiprocket/track/order/${encodeURIComponent(orderId)}`);
    },

    async trackByShipmentId(shipmentId: string) {
        return apiCall<any>(`/shiprocket/track/shipment/${encodeURIComponent(shipmentId)}`);
    },

    async getCouriers() {
        return apiCall<any[]>('/shiprocket/couriers');
    },

    async checkServiceability(payload: any) {
        return apiCall<any>('/shiprocket/couriers/serviceability', {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    },

    async assignBatchAwb(shipmentIds: string[]) {
        return apiCall<any>('/shiprocket/batch/assign-awb', {
            method: 'POST',
            body: JSON.stringify({ shipmentIds }),
        });
    },

    async generateBatchPickup(shipmentIds: string[]) {
        return apiCall<any>('/shiprocket/batch/generate-pickup', {
            method: 'POST',
            body: JSON.stringify({ shipmentIds }),
        });
    },

    async generateLabel(shipmentIds: string[]) {
        return apiCall<any>('/shiprocket/labels/generate', {
            method: 'POST',
            body: JSON.stringify({ shipmentIds }),
        });
    },

    async downloadLabel(shipmentId: string) {
        return apiCall<any>(`/shiprocket/labels/${shipmentId}`);
    },

    async generateManifest(shipmentIds: string[]) {
        return apiCall<any>('/shiprocket/manifests/generate', {
            method: 'POST',
            body: JSON.stringify({ shipmentIds }),
        });
    },

    async createPickupAddress(payload: any) {
        return apiCall<any>('/shiprocket/pickup-addresses', {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    },

    async updatePickupAddress(addressId: string, payload: any) {
        return apiCall<any>(`/shiprocket/pickup-addresses/${addressId}`, {
            method: 'PUT',
            body: JSON.stringify(payload),
        });
    },

    async deletePickupAddress(addressId: string) {
        return apiCall<any>(`/shiprocket/pickup-addresses/${addressId}`, {
            method: 'DELETE',
        });
    },

    async getRates(payload: any) {
        return apiCall<any>('/shiprocket/rates', {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    },

    async getNdr(limit?: number, offset?: number) {
        const searchParams = new URLSearchParams();
        if (limit !== undefined) searchParams.append('limit', String(limit));
        if (offset !== undefined) searchParams.append('offset', String(offset));
        const query = searchParams.toString() ? `?${searchParams.toString()}` : '';
        return apiCall<any[]>(`/shiprocket/ndr${query}`);
    },

    async resolveNdr(awbCode: string, resolution: any) {
        return apiCall<any>(`/shiprocket/ndr/${encodeURIComponent(awbCode)}/resolve`, {
            method: 'POST',
            body: JSON.stringify({ resolution }),
        });
    },

    async createReturn(payload: any) {
        return apiCall<any>('/shiprocket/returns/create', {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    },

    async trackReturn(returnOrderId: string) {
        return apiCall<any>(`/shiprocket/returns/${encodeURIComponent(returnOrderId)}`);
    },
};

// Health check
export async function checkApiHealth() {
    return apiCall<{ status: string; message: string }>('/health');
}

export default {
    auth: authApi,
    products: productsApi,
    categories: categoriesApi,
    orders: ordersApi,
    cart: cartApi,
    banners: bannersApi,
    panchang: panchangApi,
    coupons: couponsApi,
    customers: customersApi,
    settings: settingsApi,
    festivals: festivalsApi,
    shiprocket: shiprocketApi,
    checkHealth: checkApiHealth,
};
